import { enabled } from "@giftforward/entitlements";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useMyEntitlements } from "@/hooks/useMyEntitlements";
import { useDeleteReminder, useReminders, useSaveReminders } from "@/hooks/useReminders";
import type { Reminder } from "@/interfaces/reminders";
import {
  combineDateTime,
  formatDisplayDate,
  hasChronologicalOrder,
  hasPastDate,
  parseTime,
  reminderDateBounds,
} from "@/lib/reminders";
import { scheduleZoneLabel } from "@/utils/timezone-helper";
import ReminderCustomDatesUpsellModal from "./ReminderCustomDatesUpsellModal";
import ReminderEmailUpcoming, { FADE_MS } from "./ReminderEmailUpcoming";

const MAX_UPCOMING = 4;

// ── Public types ──────────────────────────────────────────────────────────────
export interface ReminderEmailsModalProps {
  open: boolean;
  onClose: () => void;
  campaignId: string;
  /** May the caller edit THIS campaign's reminders? False opens them read-only. */
  canEdit: boolean;
  onSave?: (reminders: Reminder[]) => void;
}

export type { Reminder };

// ── Modal ─────────────────────────────────────────────────────────────────────

const OVERLINE: object = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "1.98px",
  color: "var(--color-dark-dust)",
  display: "block",
  mb: 1.5,
};

export default function ReminderEmailsModal({
  open,
  onClose,
  campaignId,
  canEdit,
  onSave,
}: ReminderEmailsModalProps) {
  const { ent } = useMyEntitlements();
  const canCustomizeDates = !!ent && enabled(ent, "campaign_reminders", "custom_dates");
  const [upcoming, setUpcoming] = useState<Reminder[]>([]);
  const [fadingIds, setFadingIds] = useState<Set<string>>(new Set());
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [upsellOpen, setUpsellOpen] = useState(false);

  const { data: persistedReminders, isLoading: remindersLoading } = useReminders(campaignId, open);
  const {
    mutateAsync: doSave,
    isPending: isSaving,
    error: saveError,
  } = useSaveReminders(campaignId);
  const { mutateAsync: doDelete, isPending: isDeleting } = useDeleteReminder(campaignId);

  // Seed upcoming list from persisted data whenever the modal opens
  useEffect(() => {
    if (open && persistedReminders) {
      setUpcoming(persistedReminders.upcoming);
      setFadingIds(new Set());
      setValidationError(null);
      setSaveSuccess(false);
    }
  }, [open, persistedReminders]);

  const sentReminders = persistedReminders?.sent ?? [];

  // Calendar bounds per row, from the same helper Step 2 uses, so neither surface can offer a day
  // the save would reject. Two wrinkles are specific to this one. The window arrives from the
  // server already resolved — a launched ASAP campaign measures from its real launch instant, not
  // from a scheduled date it never had. And this list holds ONLY the upcoming rows, so the first
  // one's floor is the last reminder that already went out rather than the campaign's send date:
  // nothing can be scheduled before a reminder recipients have already received.
  // Every date here is in the CAMPAIGN's zone — split that way by the fetch and read that way by the
  // save — so a row shows the same time the builder and the e-mails do, wherever the sender sits.
  const timeZone = persistedReminders?.timeZone;
  const lastSent = sentReminders.at(-1);
  const floorIso =
    (lastSent ? combineDateTime(lastSent.date, lastSent.time, timeZone) : null) ??
    persistedReminders?.effectiveSendDate ??
    null;
  const upcomingIsoDates = upcoming.map((r) => combineDateTime(r.date, r.time, timeZone));

  function boundsFor(idx: number): { minDate?: string; maxDate?: string } {
    if (floorIso === null) return {};
    return reminderDateBounds(
      upcomingIsoDates,
      idx,
      floorIso,
      persistedReminders?.expireDate ?? null,
      timeZone,
    );
  }
  const maxReached = upcoming.length >= MAX_UPCOMING;
  const hasIncomplete = upcoming.some((r) => !r.date || !r.time);

  const isPendingMutation = isSaving || isDeleting;
  // RBAC/in-flight block, independent of plan — a non-Pro sender with edit rights still gets a
  // clickable (if ultimately refused) Add button below, so the upsell can explain why.
  const cannotModify = isPendingMutation || !canEdit;
  const rowsDisabled = cannotModify || !canCustomizeDates;
  const isAddDisabled = maxReached || cannotModify;
  const isSaveDisabled = hasIncomplete || cannotModify || !canCustomizeDates;

  function addReminder() {
    if (maxReached) return;
    if (!canCustomizeDates) {
      setUpsellOpen(true);
      return;
    }
    setValidationError(null);
    setUpcoming((prev) => [...prev, { id: crypto.randomUUID(), date: "", time: "" }]);
  }

  async function deleteReminder(id: string) {
    setValidationError(null);

    // Persisted reminders have ids like "reminder-1", "reminder-2", etc.
    const persistedMatch = id.match(/^reminder-(\d)$/);
    if (persistedMatch) {
      const reminderNumber = Number(persistedMatch[1]);
      try {
        await doDelete(reminderNumber);
      } catch (err) {
        setValidationError(err instanceof Error ? err.message : "Failed to delete reminder.");
        return;
      }
    }

    setFadingIds((prev) => new Set([...prev, id]));
    setTimeout(() => {
      setUpcoming((prev) => prev.filter((r) => r.id !== id));
      setFadingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, FADE_MS);
  }

  function updateReminder(id: string, date: string, time: string) {
    setValidationError(null);
    setUpcoming((prev) => prev.map((r) => (r.id === id ? { ...r, date, time } : r)));
  }

  async function handleSave() {
    setValidationError(null);
    setSaveSuccess(false);

    if (hasPastDate(upcoming, timeZone)) {
      setValidationError("Reminder dates must be in the future.");
      return;
    }

    if (!hasChronologicalOrder(upcoming, timeZone)) {
      setValidationError("Reminder dates must be in chronological order.");
      return;
    }

    try {
      await doSave(upcoming.map(({ date, time }) => ({ date, time })));
      setSaveSuccess(true);
      onSave?.(upcoming);
      setTimeout(onClose, 800);
    } catch (err) {
      setValidationError(err instanceof Error ? err.message : "Failed to save reminders.");
    }
  }

  const errorMessage = validationError ?? (saveError instanceof Error ? saveError.message : null);

  return (
    <Dialog
      open={open}
      onClose={isPendingMutation ? undefined : onClose}
      PaperProps={{
        sx: {
          borderRadius: "var(--radius-card)",
          border: "1px solid var(--color-smoke)",
          width: 711,
          maxWidth: 711,
          boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
          overflowX: "hidden",
        },
      }}
    >
      <Box sx={{ p: "20px" }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Typography
            sx={{ flex: 1, fontSize: 18, fontWeight: 600, color: "var(--color-rodeo-black)" }}
          >
            Reminder Emails
          </Typography>
          <IconButton
            onClick={onClose}
            disabled={isPendingMutation}
            size="small"
            sx={{
              border: "1px solid var(--color-smoke)",
              borderRadius: "var(--radius-btn, 6px)",
              width: 52,
              height: 52,
              color: "var(--color-rodeo-black)",
              "&:hover": { bgcolor: "var(--color-wax)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </Box>

        {/* Loading skeleton */}
        {remindersLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress
              aria-label="Loading"
              size={28}
              sx={{ color: "var(--color-dark-dust)" }}
            />
          </Box>
        )}

        {!remindersLoading && (
          <>
            {/* Sent */}
            {sentReminders.length > 0 && (
              <Box sx={{ mb: 0 }}>
                <Typography component="span" sx={OVERLINE}>
                  Sent
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {sentReminders.map((r) => {
                    const { h, m, ap } = parseTime(r.time);
                    const timeLabel = `${parseInt(h, 10)}:${m} ${ap}`;
                    return (
                      <Box key={r.id} sx={{ display: "flex", alignItems: "center" }}>
                        <Typography sx={{ fontSize: 16, color: "var(--color-rodeo-black)" }}>
                          {formatDisplayDate(r.date)}&nbsp;&nbsp;•&nbsp;&nbsp;{timeLabel}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <CheckCircleOutlineIcon
                            sx={{ fontSize: 18, color: "var(--color-dark-dust)" }}
                          />
                          <Typography sx={{ fontSize: 14, color: "var(--color-graphite)" }}>
                            Sent
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {sentReminders.length > 0 && (
              <Box sx={{ borderBottom: "1px solid var(--color-smoke)", my: 2.5 }} />
            )}

            {/* Upcoming */}
            <Box sx={{ mb: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography component="span" sx={OVERLINE}>
                  Upcoming
                </Typography>
                {!canCustomizeDates && (
                  <Typography
                    onClick={() => setUpsellOpen(true)}
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "var(--color-dark-dust)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      "&:hover": { opacity: 0.7 },
                    }}
                  >
                    <LockOutlinedIcon sx={{ fontSize: 14 }} />
                    Upgrade to Pro to customize
                  </Typography>
                )}
              </Box>
              {timeZone && (
                <Typography sx={{ fontSize: 12, color: "var(--color-graphite)", mb: 1 }}>
                  Times are in {scheduleZoneLabel(timeZone)}, the campaign's time zone.
                </Typography>
              )}

              {upcoming.length === 0 ? (
                <Typography sx={{ fontSize: 14, color: "var(--color-graphite)", py: 0.5 }}>
                  No upcoming reminders
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {upcoming.map((r, idx) => (
                    <ReminderEmailUpcoming
                      key={r.id}
                      reminder={r}
                      isFading={fadingIds.has(r.id)}
                      disabled={rowsDisabled}
                      onDelete={() => deleteReminder(r.id)}
                      onChange={(date, time) => updateReminder(r.id, date, time)}
                      {...boundsFor(idx)}
                    />
                  ))}
                </Box>
              )}
            </Box>

            {/* Validation / API error */}
            {errorMessage && (
              <Alert severity="error" sx={{ mt: 2, fontSize: 13 }}>
                {errorMessage}
              </Alert>
            )}

            {/* Success feedback */}
            {saveSuccess && !errorMessage && (
              <Alert severity="success" sx={{ mt: 2, fontSize: 13 }}>
                Reminders saved successfully!
              </Alert>
            )}

            {/* Footer */}
            <Box sx={{ display: "flex", alignItems: "center", pt: 2, mt: 2.5 }}>
              <Button
                startIcon={<AddIcon sx={{ fontSize: "14px !important" }} />}
                onClick={addReminder}
                disabled={isAddDisabled}
                sx={{
                  textTransform: "uppercase",
                  color: maxReached ? "var(--color-smoke)" : "var(--color-graphite)",
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  border: "none",
                  px: 0,
                  py: 0,
                  minWidth: 0,
                  "&:hover": { bgcolor: "transparent", opacity: maxReached ? 1 : 0.7 },
                  "&.Mui-disabled": { color: "var(--color-smoke)" },
                }}
              >
                Add Reminder
              </Button>
              <Typography
                sx={{
                  ml: 1.5,
                  fontSize: 14,
                  fontWeight: 600,
                  color: maxReached ? "var(--color-good-wine)" : "var(--color-graphite)",
                }}
              >
                {upcoming.length}/{MAX_UPCOMING}
              </Typography>

              <Box sx={{ flex: 1 }} />

              <Button
                onClick={handleSave}
                disabled={isSaveDisabled}
                variant="contained"
                disableElevation
                startIcon={
                  isSaving ? (
                    <CircularProgress aria-label="Loading" size={14} sx={{ color: "white" }} />
                  ) : undefined
                }
                sx={{
                  textTransform: "none",
                  bgcolor: "var(--color-dark-dust)",
                  color: "white",
                  borderRadius: "var(--radius-btn, 6px)",
                  px: 3,
                  py: 1.25,
                  fontSize: 14,
                  fontWeight: 600,
                  "&:hover": { bgcolor: "var(--color-rodeo-dust)" },
                  "&.Mui-disabled": {
                    bgcolor: "var(--color-smoke)",
                    color: "var(--color-graphite)",
                  },
                }}
              >
                {isSaving ? "Saving…" : "Save"}
              </Button>
            </Box>
          </>
        )}
      </Box>
      <ReminderCustomDatesUpsellModal open={upsellOpen} onClose={() => setUpsellOpen(false)} />
    </Dialog>
  );
}
